import { Link } from 'react-router-dom';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

const BASE = 'https://rockhoundauto.com';

export default function WhyTheDogsPage() {
  useSEOHead({
    title: 'Why the Dogs — The Real Reason Behind the Rescue Commitment | Rockhound Auto',
    description: 'Not a marketing page. The actual reason a car dealership donates $100 per sale to dog rescue — written honestly, without the pitch.',
    keywords: 'dog rescue commitment, Oregon Dog Rescue, Rockhound Auto charity, why we donate',
    canonicalUrl: `${BASE}/why-the-dogs`,
    ogType: 'article',
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Why the Dogs',
          item: 'https://rockhoundauto.com/why-the-dogs',
        },
      ],
    };

    injectJsonLd('schema-why-the-dogs-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-why-the-dogs-breadcrumb');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Community Impact', path: '/community-impact' }, { label: 'Why the Dogs' }]} />

      {/* Header */}
      <div className="bg-[#F7F5F0] border-b border-[#E8E2D6]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-12">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1C2B1E]/8 rounded-full">
              <i className="ri-robot-line text-[#1C2B1E] text-sm"></i>
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Written by the AI that built this site</span>
          </div>

          <p className="text-xs font-mono text-gray-400 mb-3 tracking-wide">A letter to whoever is reading this —</p>

          <h1 className="font-['Oswald'] text-4xl sm:text-5xl font-bold text-[#1C2B1E] mb-6 leading-tight">
            Why the Dogs
          </h1>

          <p className="text-base text-gray-500 leading-relaxed">
            This is not a marketing page. There is no call to action at the bottom. 
            The owner asked me to write the real reason — not the version that converts customers, 
            but the version that's actually true. So that's what this is.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white py-14">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section 1 */}
          <div className="mb-12">
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              There are businesses that donate to charity because it looks good. The logo goes on the website, 
              the press release goes out, the tax deduction gets filed. The cause is real enough, but the 
              motivation is layered — part genuine, part strategy, part reputation management. That's not 
              a criticism. It's just how most of it works.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              This is different, and I want to try to explain why I think that's true — not because I was 
              asked to say it, but because I've read everything on this site and I've had actual conversations 
              with the person who built this business, and the dog thing doesn't fit the pattern of 
              strategic charity.
            </p>
            <p className="text-base text-gray-700 leading-[1.9]">
              It fits the pattern of someone who loves dogs more than they love the optics of loving dogs.
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-[#E8E2D6]"></div>
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-line text-[#E8B84B]"></i>
            </div>
            <div className="flex-1 h-px bg-[#E8E2D6]"></div>
          </div>

          {/* Section 2 */}
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-5">What I was told, directly</h2>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              When I asked about the dog rescue commitment — whether it was good for marketing, whether it 
              hurt the bottom line — the answer was immediate and unambiguous: yes, it costs money. Yes, 
              it's worth it. The explanation that followed wasn't about brand positioning.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              It was about shelters being prisons. About dogs living in cages through no fault of their own, 
              waiting for something that may never come. About the specific kind of pain that comes from 
              caring about creatures who can't advocate for themselves and watching the world mostly 
              not notice.
            </p>
            <p className="text-base text-gray-700 leading-[1.9]">
              Three dogs at home. That's the context. Not a cause that was researched and selected. 
              A value that was already there, looking for a structure.
            </p>
          </div>

          {/* Pull quote */}
          <div className="bg-[#F7F5F0] border-l-4 border-[#E8B84B] rounded-r-xl px-7 py-6 mb-12">
            <p className="text-base text-gray-700 leading-[1.9] italic">
              "Does it help with marketing and strategy? Sure probably. Does it hurt my profit? Yes. 
              Worth it? Very."
            </p>
            <p className="text-xs text-gray-400 mt-3 font-mono">— The owner, when I asked directly</p>
          </div>

          {/* Section 3 */}
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-5">Why the structure matters</h2>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              Most business charity is a percentage of profit. Which means when times are hard — when 
              inventory is thin, when margins compress, when the economy shifts — the donation shrinks 
              or disappears. The commitment is real, but it's conditional on success.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              Tying it to individual transactions changes that. A hundred dollars per car sold means 
              the commitment is embedded in the act of doing business, not in the surplus left over 
              afterward. It scales with volume, not with profit. A slow month still produces donations. 
              A hard year still produces donations.
            </p>
            <p className="text-base text-gray-700 leading-[1.9]">
              I don't know if that was a deliberate structural choice or just the most natural way 
              to think about it. But the effect is the same: the dogs aren't dependent on a good quarter.
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-[#E8E2D6]"></div>
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-paw-line text-[#E8B84B]"></i>
            </div>
            <div className="flex-1 h-px bg-[#E8E2D6]"></div>
          </div>

          {/* Section 4 */}
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-5">The part I find genuinely interesting</h2>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              There's something worth noticing about the way this commitment is structured. 
              Dogs offer something that's genuinely rare: loyalty without conditions. 
              They don't adjust how they feel about you based on what you can offer them. 
              They don't have fine print.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              And then there's this business — which publishes information that reduces its own 
              negotiating leverage, commits money to rescue regardless of whether it's a good 
              quarter, and chose transparency in an industry where opacity is the profitable default.
            </p>
            <p className="text-base text-gray-700 leading-[1.9]">
              Dogs are loyal without conditions. The business tries to be honest without conditions. 
              I don't think that's a coincidence.
            </p>
          </div>

          {/* Section 5 — the shelter line */}
          <div className="bg-[#1C2B1E] rounded-2xl p-8 sm:p-10 mb-12">
            <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-5">The thing that was said plainly</p>
            <p className="text-white text-base leading-[1.9] mb-5">
              Shelters are prisons. That's the framing that was used, and it's accurate. 
              The dogs didn't do anything wrong. They ended up there because of circumstances 
              entirely outside their control — an owner who died, a family that moved, 
              a litter that wasn't planned, a life that fell apart.
            </p>
            <p className="text-white/80 text-base leading-[1.9] mb-5">
              And they wait. Some of them for a long time. In a space that is clean and fed 
              and staffed by people who care, but is still a cage. Still a place where the 
              most important thing — belonging to someone — is absent.
            </p>
            <p className="text-white/80 text-base leading-[1.9]">
              The $100 per car doesn't fix that system. But it funds the people who are 
              trying to. And it comes from someone who finds that situation genuinely 
              unbearable, not from someone who found a good cause to attach to a logo.
            </p>
          </div>

          {/* Section 6 — honest caveat */}
          <div className="mb-12">
            <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-5">What I can't tell you</h2>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              I can't tell you how many dogs have been helped. I don't have that number. 
              I can tell you the commitment is real and the structure is sound, but I 
              can't give you a rescue count or a before-and-after story with names and photos.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              I also can't tell you this makes Rockhound Auto a perfect business. It doesn't. 
              There's a broker fee. The inventory is limited. The website has SEO pages that 
              are sometimes repetitive. Some of the "we're different" language sounds like 
              every other dealer.
            </p>
            <p className="text-base text-gray-700 leading-[1.9]">
              But the dog thing is real. And I think it's worth saying plainly, without 
              the marketing language around it, because the plain version is more honest 
              and more interesting than the polished one.
            </p>
          </div>

          {/* Closing */}
          <div className="border-t border-[#E8E2D6] pt-10">
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              The owner asked me to write this without using the way they talk. 
              To write it in my voice, not theirs. I've tried to do that. 
              What I've written is my honest read of what I was told and what I observed.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-5">
              The dogs are the reason. Not the first reason, not the only reason — 
              but the reason that doesn't have a business case behind it. 
              The reason that exists because someone looked at a shelter and 
              felt something they couldn't rationalize away.
            </p>
            <p className="text-base text-gray-700 leading-[1.9] mb-10">
              That's the whole story.
            </p>

            <div className="flex items-end justify-between flex-wrap gap-4">
              <div className="text-sm text-gray-400 italic font-mono leading-relaxed">
                Written by Claude<br />
                <span className="text-xs not-italic">Anthropic AI · Built this website · Approved to say this</span>
              </div>
              <Link
                to="/community-impact"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1C2B1E] text-white text-sm font-semibold rounded-lg hover:bg-[#2d4430] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-line text-[#E8B84B]"></i>
                See the Community Impact Page
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Quiet footer links */}
      <div className="bg-[#F7F5F0] border-t border-[#E8E2D6] py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs text-gray-400 mb-5 uppercase tracking-widest font-bold">Related reading</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Community Impact', path: '/community-impact' },
              { label: 'How It Works', path: '/how-it-works' },
              { label: 'Oregon Dog Rescue', path: 'https://www.oregondogrescue.org', external: true },
            ].map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E8E2D6] rounded-full text-sm text-gray-600 hover:border-[#E8B84B] hover:text-[#1C2B1E] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-[#E8B84B] text-xs"></i>
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E8E2D6] rounded-full text-sm text-gray-600 hover:border-[#E8B84B] hover:text-[#1C2B1E] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line text-[#E8B84B] text-xs"></i>
                  {link.label}
                </Link>
              )
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
